import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, ArrowLeft, Heart, Sparkle, Activity } from 'lucide-react';
import { adminListUsers, getUserProfile, type AdminUserRow, type UserProfile } from '../../services/adminService';

/**
 * Admin User tab — search by email (or browse recent signups), then deep-dive a user: the agents they have the
 * strongest Friendship with, their inferred interests, 30-day activity, and wallet. Read-only; full email is
 * admin-only. Two views: a searchable list → a per-user detail panel.
 */
const UsersPanel: React.FC = () => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null); // uid

  const load = useCallback((search: string) => {
    setRows(null);
    adminListUsers(search || undefined).then((r) => setRows(r.users)).catch(() => setRows([]));
  }, []);
  useEffect(() => { const id = setTimeout(() => load(q.trim().toLowerCase()), 350); return () => clearTimeout(id); }, [q, load]);

  if (selected) return <UserDetail uid={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="font-semibold">Users <span className="text-secondary-400 font-normal text-sm">· search &amp; deep-dive</span></h2>
        <div className="relative w-72 max-w-full">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-secondary-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email…"
            className="w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 pl-8 pr-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
        </div>
      </div>
      {rows === null ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-secondary-400">{q ? 'No users match that email.' : 'No users yet.'}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-secondary-400 text-left text-xs uppercase tracking-wide">
            <tr><th className="py-1">User</th><th>Email</th><th>Joined</th><th>Last sign in</th></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.uid} onClick={() => setSelected(u.uid)}
                className="border-t border-secondary-100 dark:border-secondary-800 cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-900/40">
                <td className="py-1.5 font-medium text-secondary-900 dark:text-white">{u.name || '—'}</td>
                <td className="text-secondary-500">{u.email || '—'}</td>
                <td className="text-secondary-400 whitespace-nowrap text-xs">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                <td className="text-secondary-400 whitespace-nowrap text-xs">{u.lastSignIn ? new Date(u.lastSignIn).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!q && rows && rows.length > 0 && <p className="mt-3 text-[11px] text-secondary-400">Most recent signups · type an email to search.</p>}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="rounded-lg border border-secondary-200 dark:border-secondary-800 px-3 py-2 text-center">
    <div className="text-lg font-semibold text-secondary-900 dark:text-white">{value}</div>
    <div className="text-[11px] text-secondary-500">{label}</div>
  </div>
);

const UserDetail: React.FC<{ uid: string; onBack: () => void }> = ({ uid, onBack }) => {
  const [p, setP] = useState<UserProfile | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { getUserProfile({ uid }).then(setP).catch((e) => setErr(e?.message || 'Failed to load')); }, [uid]);

  return (
    <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200"><ArrowLeft className="w-4 h-4" /> Back to users</button>
      {err ? <p className="text-sm text-rose-500">{err}</p> : !p ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>
      ) : (
        <div className="space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">{p.user.name || p.user.email || p.user.uid}</h2>
            <p className="text-sm text-secondary-500">
              {p.user.email} · plan {p.user.plan}
              {p.user.createdAt ? ` · joined ${new Date(p.user.createdAt).toLocaleDateString()}` : ''}
              {p.user.lastSignIn ? ` · last sign in ${new Date(p.user.lastSignIn).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}` : ''}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="turns (30d)" value={p.activity.requests.toLocaleString()} />
            <Stat label="specialists" value={p.activity.distinctAgents} />
            <Stat label="WP balance" value={p.wallet?.wp ?? '—'} />
          </div>

          {p.interests.length > 0 && (
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-secondary-400"><Sparkle className="w-3.5 h-3.5" /> Interests</div>
              <div className="flex flex-wrap gap-1.5">
                {p.interests.map((i) => <span key={i} className="rounded-full bg-secondary-100 dark:bg-secondary-800 px-2.5 py-0.5 text-xs text-secondary-700 dark:text-secondary-200">{i}</span>)}
              </div>
            </div>
          )}

          {p.topAgents.length > 0 && (
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-secondary-400"><Heart className="w-3.5 h-3.5" /> Closest specialists (Friendship)</div>
              <div className="space-y-2">
                {p.topAgents.map((a) => (
                  <div key={a.agentId} className="flex items-center gap-3">
                    <div className="w-40 shrink-0">
                      <div className="text-sm font-medium text-secondary-800 dark:text-secondary-100 truncate">{a.name}</div>
                      <div className="text-[11px] text-secondary-500 truncate">{a.persona}</div>
                    </div>
                    <div className="flex-1 h-2 rounded-full bg-secondary-100 dark:bg-secondary-800 overflow-hidden">
                      <div className="h-full bg-secondary-900 dark:bg-white" style={{ width: `${Math.min(100, (a.level / 20) * 100)}%` }} />
                    </div>
                    <div className="w-12 text-right text-xs text-secondary-500 tabular-nums">{a.level}/20</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {p.recentEvents.length > 0 && (
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-secondary-400"><Activity className="w-3.5 h-3.5" /> Recent turns (masked)</div>
              <div className="space-y-1.5 max-h-72 overflow-auto">
                {p.recentEvents.map((e, i) => (
                  <div key={i} className="rounded-lg border border-secondary-100 dark:border-secondary-800 px-3 py-2 text-xs">
                    <div className="text-secondary-400">{e.agentName} · {e.tier}{e.createdAt ? ` · ${new Date(e.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}` : ''}</div>
                    <div className="text-secondary-700 dark:text-secondary-300 truncate">{e.prompt}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsersPanel;
